import {
	Grid,
	Typography,
	useTheme,
	Skeleton,
	Fade
} from "@mui/material";
import { StyleSheet, Platform } from "react-native";
import { Product } from "../models/DB";
import { ProductItem } from "./ProductItem";
import { navigationStore } from "../app/stores/Navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AutoLoadButton } from "./AutoLoadButton";

export type GridDensity = 'compact' | 'standard' | 'comfortable';

const GRID_BREAKPOINTS: Record<GridDensity, { xs: number; sm: number; lg: number }> = {
	compact: { xs: 3.5, sm: 2.5, lg: 1.8 },
	standard: { xs: 5.35, sm: 3.5, lg: 2.5 },
	comfortable: { xs: 11, sm: 5.5, lg: 3.5 },
};

const GRID_GAPS: Record<GridDensity, { xs: number; md: number }> = {
	compact: { xs: 1.5, md: 2 },
	standard: { xs: 3, md: 4 },
	comfortable: { xs: 3, md: 5 },
};

interface ProductItemProps {
	products: Product[];
	selectedCategory: string;
	navigation: any;
	onLoadingComplete?: () => void;
	gridDensity?: GridDensity;
}

const ProductSkeleton = ({ gridDensity = 'standard' }: { gridDensity?: GridDensity }) => {
	const theme = useTheme();
	const skeletonItems = Array.from({ length: 12 }, (_, i) => i);
	const bp = GRID_BREAKPOINTS[gridDensity];

	return (
		<>
			{skeletonItems.map((item) => (
				<Grid
					item
					key={`skeleton_${item}`}
					xs={bp.xs}
					sm={bp.sm}
					lg={bp.lg}
					sx={{
						backgroundColor: theme.customColors.cardBackground,
						flexDirection: "column",
					}}
					boxShadow={"0px 4px 12px rgba(0, 0, 0, 0.1)"}
					borderColor={theme.palette.divider}
					borderRadius={4}
					padding={1.5}
				>
					<Grid item marginBottom={2}>
						<Skeleton
							variant="rectangular"
							width="100%"
							height={0}
							sx={{
								paddingTop: "100%",
								borderRadius: 2,
							}}
						/>
					</Grid>
					<Grid item>
						<Skeleton variant="text" width="80%" height={24} />
						<Skeleton variant="text" width="40%" height={20} />
					</Grid>
				</Grid>
			))}
		</>
	);
};

export const ProductsLayout = ({
	products,
	navigation,
	onLoadingComplete,
	selectedCategory,
	gridDensity = 'standard',
}: ProductItemProps) => {
	const { sideMenuIsExpanded } = navigationStore(state => state);
	const theme = useTheme();
	const [windowDimensions, setWindowDimensions] = useState({
		width: typeof window !== "undefined" ? window.innerWidth : 1024,
		height: typeof window !== "undefined" ? window.innerHeight : 768
	});
	const [visible, setVisible] = useState(true);
	const gap = useMemo(() => GRID_GAPS[gridDensity], [gridDensity]);

	// Function to handle window resize events
	useEffect(() => {
		// Only run this code on web platform
		if (Platform.OS === "web") {
			const handleResize = () => {
				setWindowDimensions({
					width: window.innerWidth,
					height: window.innerHeight
				});
			};

			window.addEventListener("resize", handleResize);
			return () => window.removeEventListener("resize", handleResize);
		}
	}, []);

	// Lazy load products to improve performance
	const [visibleProducts, setVisibleProducts] = useState<Product[]>([]);
	const [loadingMore, setLoadingMore] = useState(false);
	const BATCH_SIZE = 12;

	useEffect(() => {
		// Reset visible products when products list changes
		setVisibleProducts([]);
		loadMoreProducts(true);
	}, [products]);

	useEffect(() => {
		setVisible(false);
		const timeout = setTimeout(() => {
			setVisible(true);
		}, 300);

		return () => clearTimeout(timeout);
	}, [selectedCategory]);

	const loadMoreProducts = useCallback((isInitial = false) => {
		if (loadingMore && !isInitial) return;

		setLoadingMore(true);
		setTimeout(() => {
			const currentLength = visibleProducts.length;
			const newProducts = products.slice(0, currentLength + BATCH_SIZE);

			setVisibleProducts(newProducts);
			setLoadingMore(false);

			// Call onLoadingComplete callback if it exists and this is the initial load
			if (isInitial && onLoadingComplete) {
				onLoadingComplete();
			}
		}, 50); // Small timeout to prevent UI blocking
	}, [products, visibleProducts, loadingMore, onLoadingComplete]);

	// Empty list display
	if (products.length === 0) {
		// Call onLoadingComplete to notify parent that loading is done
		if (onLoadingComplete) {
			onLoadingComplete();
		}

		return (
			<Typography
				variant="body1"
				sx={{
					...styles.subtitle,
					color: theme.palette.text.primary,
				}}
			>
				{`No items available`}
			</Typography>
		);
	}

	return (
		<Fade in={visible} easing={{ enter: "ease-in-out", exit: "ease-in-out" }} timeout={400}>
			<Grid
				container
				gap={{ xs: gap.xs, md: gap.md }}
				justifyContent={{ xs: "center", md: "flex-start" }}
				marginLeft={{ xs: 0, md: sideMenuIsExpanded ? 0 : 4 }}
				sx={{
					flex: 1,
					transition: "all 0.3s ease-in-out",
				}}
				marginBottom={8}
			>
				{visibleProducts.length === 0 && loadingMore ? (
					<ProductSkeleton gridDensity={gridDensity} />
				) : (
					visibleProducts.map((product, index) => (
						<ProductItem
							key={`productItem_${index}_${product.id}`}
							product={product}
							navigation={navigation}
							gridDensity={gridDensity}
						/>
					))
				)}
				<Grid
					item
					xs={12}
					container
					justifyContent="center"
					marginTop={4}
					marginBottom={2}
				>
					<AutoLoadButton
						onLoadMore={() => loadMoreProducts()}
						isLoading={loadingMore}
						isVisible={visibleProducts.length < products.length}
					/>
				</Grid>
			</Grid>
		</Fade>
	);
};

const styles = StyleSheet.create({
	background: {
		flex: 1,
	},
	image: {
		marginTop: 20,
		width: 50,
		height: 50,
		resizeMode: "contain",
		alignSelf: "center",
	},
	screenName: {
		margin: 16,
		alignSelf: "center",
		fontWeight: "500",
	},
	name: {
		fontSize: 16,
		fontWeight: "600",
		letterSpacing: 0.5,
	},
	price: {
		fontSize: 14,
		fontWeight: "600",
		letterSpacing: 0.5,
	},
	title: {
		flex: 1,
		fontSize: 26,
		fontWeight: "600",
		letterSpacing: 1,
		color: "black",
	},
	subtitle: {
		fontSize: 14,
		fontWeight: "400",
		letterSpacing: 0.5,
	},
	chip: {
		marginRight: 8,
	},
});
